import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosApi from "../server/service"; // Corrected import path as per your file structure

import toast from "react-hot-toast";

import {
  Input,
  Button,
  Badge,
  ShadcnTable,
  ShadcnTableHeader,
  ShadcnTableBody,
  ShadcnTableRow,
  ShadcnTableHead,
  ShadcnTableCell,
  DesignActionMenu,
} from "../component/ui/TableComponent";

import {
  Copy,
  Package,
  ArrowLeft,
  Plus,
  Search,
  Loader2,
  Pen,
} from "lucide-react";

interface ApiDesign {
  _id: string;
  status: "Active" | "Inactive";
  basicInfo: {
    designNo?: string;
    clientName?: string;
    designCategory?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface DesignData {
  id: string;
  designNo: string;
  clientName: string;
  category: string;
  status: "Active" | "Inactive";
  lastUpdated: string;
}

// --- HELPER FUNCTION ---
const formatDateTime = (iso?: string) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const DesignMaster: React.FC = () => {
  const navigate = useNavigate();
  // --- STATE MANAGEMENT ---
  const [designs, setDesigns] = useState<DesignData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    designNo: string;
  } | null>(null);

  const totalPages = Math.ceil(totalCount / pageSize);

  //  STATES FOR DUPLICATION MODAL
  const [itemToDuplicate, setItemToDuplicate] = useState<{
    id: string;
    designNo: string;
  } | null>(null);
  const [newDesignNumberInput, setNewDesignNumberInput] = useState("");
  const [isDuplicating, setIsDuplicating] = useState(false);

  // --- DATA FETCHING ---
  const fetchDesigns = useCallback(
    async (pageToFetch: number) => {
      setLoading(true);
      setFetchError(null);
      const params = new URLSearchParams({
        page: String(pageToFetch),
        limit: String(pageSize),
      });
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchInput) params.append("search", searchInput);

      try {
        const { data } = await axiosApi.get(
          `/api/designs/get-all-designs?${params.toString()}`
        );
        const mapped: DesignData[] = data.data.map((d: ApiDesign) => ({
          id: d._id,
          designNo: d.basicInfo?.designNo || "N/A",
          clientName: d.basicInfo?.clientName || "N/A",
          category: d.basicInfo?.designCategory || "N/A",
          status: d.status,
          lastUpdated: formatDateTime(d.updatedAt || d.createdAt),
        }));
        setDesigns(mapped);
        setTotalCount(data.count);
      } catch (err: any) {
        setFetchError(err?.response?.data?.message || "Failed to load designs");
        setDesigns([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, categoryFilter, statusFilter, searchInput]
  );

  // --- DELETE OPERATION ---
  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    try {
      await axiosApi.delete(`/api/designs/delete-designs/${itemToDelete.id}`);
      setTotalCount((prev) => prev - 1);
      if (designs.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchDesigns(currentPage);
      }
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to delete design.");
    } finally {
      setItemToDelete(null);
    }
  }, [itemToDelete, currentPage, designs.length, fetchDesigns]);

  // --- EFFECTS ---
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDesigns(1);
      if (currentPage !== 1) setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, categoryFilter, statusFilter]);

  useEffect(() => {
    fetchDesigns(currentPage);
  }, [currentPage]);

  const handleConfirmDuplicate = async () => {
    if (!itemToDuplicate || !newDesignNumberInput.trim()) {
      toast.error("Please enter a new design number.");
      return;
    }

    setIsDuplicating(true);
    const toastId = toast.loading("Duplicating design...");

    try {
      await axiosApi.post(`/api/designs/duplicate/${itemToDuplicate.id}`, {
        newDesignNo: newDesignNumberInput.trim(),
      });

      toast.success("Design duplicated successfully!", { id: toastId });
      setItemToDuplicate(null);
      setNewDesignNumberInput("");
      fetchDesigns(1);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to duplicate design.",
        { id: toastId }
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleEditClick = async (designId: string) => {
    const toastId = toast.loading("Preparing editor, please wait...");

    try {
      // Backend API ko call karke saare images ko validate/refresh karein
      await axiosApi.patch(`/api/designs/validate-images/${designId}`);
      toast.success("Ready to edit!", { id: toastId });

      // Safaltapoorvak hone par, edit page par navigate karein
      navigate(`/CreateNewDesign/${designId}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Could not prepare editor.", {
        id: toastId,
      });
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen  font-sans">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
        <div className="flex items-center gap-10">
          <Link
            to="/master"
            className="flex items-center gap-2 border bg-white border-slate-200 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-black font-medium">
              Back <span className="hidden sm:inline">to Masters</span>
            </span>
          </Link>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">
              Design Master
            </h1>
            <p className="text-slate-600 text-sm sm:text-base mt-1">
              Manage design templates and fabric specifications
            </p>
          </div>
        </div>

        <Button
          onClick={() => navigate("/CreateNewDesign")}
          className="bg-green-500 hover:bg-green-600 text-white"
        >
          <Plus className="w-5 h-5 mr-2" /> New Design
        </Button>
      </div>

      <div className="bg-white shadow-md rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by Design No or Client..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="all">Select category</option>
              <option value="kaftaan">Kaftaan</option>
              <option value="co-ord-set">Co-ord set</option>
              <option value="plazo">Plazo style</option>
              <option value="farshi">Farshi style</option>
              <option value="kurti">Kurti style</option>
              <option value="pant">Pant style</option>
              <option value="gharara">Gharara</option>
              <option value="patiyala">Patiyala</option>
              <option value="jacket">Jacket style</option>
              <option value="choli">Choli style</option>
              <option value="print">Print</option>
              <option value="other">Others</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white shadow-lg rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading Designs...</span>
          </div>
        ) : fetchError ? (
          <div className="p-12 text-center text-red-600 font-medium">
            {fetchError}
          </div>
        ) : designs.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            No designs found matching your criteria.
          </div>
        ) : (
          <>
            <div className="hidden lg:block">
              <ShadcnTable>
                <ShadcnTableHeader>
                  <ShadcnTableRow>
                    <ShadcnTableHead>Design No</ShadcnTableHead>
                    <ShadcnTableHead>Client Name</ShadcnTableHead>
                    <ShadcnTableHead>Category</ShadcnTableHead>
                    <ShadcnTableHead>Status</ShadcnTableHead>
                    <ShadcnTableHead>Last Updated</ShadcnTableHead>
                    <ShadcnTableHead className="text-center">
                      Actions
                    </ShadcnTableHead>
                  </ShadcnTableRow>
                </ShadcnTableHeader>
                <ShadcnTableBody>
                  {designs.map((design) => (
                    <ShadcnTableRow key={design.id}>
                      <ShadcnTableCell className="font-medium text-slate-900">
                        {design.designNo}
                      </ShadcnTableCell>
                      <ShadcnTableCell>{design.clientName}</ShadcnTableCell>
                      <ShadcnTableCell>{design.category}</ShadcnTableCell>
                      <ShadcnTableCell>
                        <Badge
                          variant={
                            design.status === "Active" ? "success" : "danger"
                          }
                        >
                          {design.status}
                        </Badge>
                      </ShadcnTableCell>
                      <ShadcnTableCell>
                        {new Date(design.lastUpdated).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </ShadcnTableCell>
                      <ShadcnTableCell className="flex justify-center">
                        <DesignActionMenu
                          design={design}
                          onDelete={() =>
                            setItemToDelete({
                              id: design.id,
                              designNo: design.designNo,
                            })
                          }
                          onDuplicate={() => {
                            setItemToDuplicate({
                              id: design.id,
                              designNo: design.designNo,
                            });
                            setNewDesignNumberInput(`${design.designNo}-copy`); // Suggest a new name
                          }}
                          onEdit={handleEditClick}
                        />
                      </ShadcnTableCell>
                    </ShadcnTableRow>
                  ))}
                </ShadcnTableBody>
              </ShadcnTable>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 lg:hidden">
              {designs.map((design) => (
                <div
                  key={design.id}
                  className="bg-white rounded-lg shadow-md border border-gray-300  p-4 flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 bg-slate-100 p-3 rounded-full">
                        {" "}
                        <Package className="text-gray-500 w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-slate-900">
                          {design.designNo}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {design.clientName}
                        </p>
                      </div>
                    </div>
                    <DesignActionMenu
                      design={design}
                      onDelete={() =>
                        setItemToDelete({
                          id: design.id,
                          designNo: design.designNo,
                        })
                      }
                      onDuplicate={() => {
                        setItemToDuplicate({
                          id: design.id,
                          designNo: design.designNo,
                        });
                        setNewDesignNumberInput(`${design.designNo}-copy`);
                      }}
                      onEdit={handleEditClick}
                    />
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Status:</span>
                      <Badge
                        variant={
                          design.status === "Active" ? "success" : "danger"
                        }
                      >
                        {design.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Category:</span>
                      <span className="font-medium text-slate-800">
                        {design.category}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Last Updated:</span>
                      <span className="font-medium text-slate-800">
                        {new Date(design.lastUpdated).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between flex-wrap gap-4 p-4 border-t">
                <span className="text-sm text-slate-600">
                  Showing <span className="font-medium">{designs.length}</span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm font-medium px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {itemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4 p-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Confirm Deletion
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete Design No:{" "}
              <span className="font-bold">{itemToDelete.designNo}</span>? This
              action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setItemToDelete(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {itemToDuplicate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4 p-6 animate-fade-in-up">
            <h2 className="text-lg font-semibold text-slate-900">
              Duplicate Design
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Create a copy of{" "}
              <span className="font-bold">{itemToDuplicate.designNo}</span>.
              Please provide a new, unique design number for the copy.
            </p>
            <div className="mt-4">
              <label
                htmlFor="newDesignNo"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                New Design Number
              </label>
              <Input
                id="newDesignNo"
                type="text"
                value={newDesignNumberInput}
                onChange={(e) => setNewDesignNumberInput(e.target.value)}
                placeholder="Enter new design number"
                autoFocus
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setItemToDuplicate(null)}
              >
                Cancel
              </Button>
              <Button onClick={handleConfirmDuplicate} disabled={isDuplicating}>
                {isDuplicating ? (
                  <Loader2 className="animate-spin w-4 h-4 mr-2" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                {isDuplicating ? "Duplicating..." : "Confirm & Duplicate"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignMaster;
